import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, Stack, Chip, CircularProgress, TextField, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, IconButton, Grid,
  Table, TableHead, TableBody, TableRow, TableCell, Divider, Link,
} from '@mui/material'
import {
  Map as MapIcon, Speed, AccessTime, PlayArrow, Info, Close,
  LocationOn, Timer, TrendingUp, Route, MyLocation, Flag,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { subDays, formatISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTrips, Trip } from '../../hooks/useFleet'
import { useDevices, Device } from '../../hooks/useDevices'
import { api } from '../../lib/api'
import { fmtDateTimeFull, fmtTime } from '../../lib/dateFormat'

interface GpsPoint {
  latitude: number
  longitude: number
  speed: number
  heading: number
  time: string
}

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${s}s`
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ── Trip Detail Dialog ───────────────────────────────────────────
function TripDetailDialog({ trip, deviceName, open, onClose }: {
  trip: Trip; deviceName: string; open: boolean; onClose: () => void
}) {
  const { t } = useTranslation()
  const { data: points = [], isLoading } = useQuery({
    queryKey: ['trip-points', trip.id],
    queryFn: () =>
      api.get<{ points: GpsPoint[] }>('/analytics/gps/track', {
        params: { device_id: trip.device_id, from: trip.started_at, to: trip.ended_at || new Date().toISOString() },
      }).then((r) => {
        const d = r.data
        if (Array.isArray(d)) return d as GpsPoint[]
        return (d as any)?.points ?? []
      }),
    enabled: open,
  })

  const stats = useMemo(() => {
    if (points.length === 0) return null
    const speeds = points.map((p: GpsPoint) => p.speed).filter((s: number) => s > 0)
    const totalSpeedSum = speeds.reduce((a: number, b: number) => a + b, 0)
    const maxSpeed = Math.max(...points.map((p: GpsPoint) => p.speed), 0)
    const avgSpeed = speeds.length > 0 ? totalSpeedSum / speeds.length : 0
    const idlePoints = points.filter((p: GpsPoint) => p.speed === 0).length
    const idlePct = (idlePoints / points.length) * 100

    // Compute distance from points if trip distance is 0
    let computedDist = trip.distance_km
    if (computedDist === 0 && points.length > 1) {
      computedDist = 0
      for (let i = 1; i < points.length; i++) {
        computedDist += haversineKm(points[i - 1].latitude, points[i - 1].longitude, points[i].latitude, points[i].longitude)
      }
    }

    return { maxSpeed, avgSpeed, idlePct, computedDist, totalPoints: points.length }
  }, [points, trip.distance_km])

  const kpis: { label: string; value: string; icon: React.ReactNode; color: string }[] = stats ? [
    { label: t('trips.start'), value: fmtDateTimeFull(trip.started_at), icon: <MyLocation />, color: '#10b981' },
    { label: t('trips.end'), value: trip.ended_at ? fmtDateTimeFull(trip.ended_at) : t('trips.inProgress'), icon: <Flag />, color: '#ef4444' },
    { label: t('trips.distance'), value: `${stats.computedDist.toFixed(2)} km`, icon: <Route />, color: '#34A8E0' },
    { label: t('trips.duration'), value: formatDuration(trip.duration_sec), icon: <Timer />, color: '#3b82f6' },
    { label: t('trips.maxSpeed'), value: `${stats.maxSpeed.toFixed(0)} km/h`, icon: <Speed />, color: '#ef4444' },
    { label: t('trips.avgSpeed'), value: `${stats.avgSpeed.toFixed(1)} km/h`, icon: <TrendingUp />, color: '#10b981' },
    { label: t('trips.gpsPoints'), value: `${stats.totalPoints}`, icon: <LocationOn />, color: '#f97316' },
    { label: t('trips.idleTime'), value: `${stats.idlePct.toFixed(1)}%`, icon: <AccessTime />, color: '#64748b' },
  ] : []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0', maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            Trip Details — {deviceName}
          </Typography>
          <Typography variant="caption" color="#94a3b8">
            {fmtDateTimeFull(trip.started_at)}
            {trip.ended_at ? ` → ${fmtDateTimeFull(trip.ended_at)}` : ` → ${t('trips.inProgress')}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#34A8E0' }} />
          </Box>
        ) : (
          <>
            {/* KPI cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {kpis.map((k) => (
                <Grid item xs={6} sm={4} md={3} key={k.label}>
                  <Card sx={{ p: 1.5, border: '1px solid #f1f5f9', borderRadius: 2, textAlign: 'center' }}>
                    <Box sx={{ color: k.color, mb: 0.5, display: 'flex', justifyContent: 'center' }}>
                      {k.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={800} color="#0f172a" sx={{ fontSize: 18 }}>
                      {k.value}
                    </Typography>
                    <Typography variant="caption" color="#94a3b8" sx={{ fontSize: 11 }}>
                      {k.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Start / End locations */}
            {trip.start_latitude != null && trip.start_longitude != null && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MyLocation sx={{ fontSize: 16, color: '#10b981' }} />
                    <Typography variant="body2" color="#475569">
                      {t('trips.start')}:{' '}
                      <Link href={`https://www.google.com/maps?q=${trip.start_latitude},${trip.start_longitude}`}
                        target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {trip.start_latitude.toFixed(5)}, {trip.start_longitude.toFixed(5)}
                      </Link>
                    </Typography>
                  </Box>
                  {trip.end_latitude != null && trip.end_longitude != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Flag sx={{ fontSize: 16, color: '#ef4444' }} />
                      <Typography variant="body2" color="#475569">
                        {t('trips.end')}:{' '}
                        <Link href={`https://www.google.com/maps?q=${trip.end_latitude},${trip.end_longitude}`}
                          target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {trip.end_latitude.toFixed(5)}, {trip.end_longitude.toFixed(5)}
                        </Link>
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* GPS points table */}
            <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ mb: 1 }}>
              {t('trips.gpsPoints')} ({points.length})
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: 12 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>{t('trips.time')}</TableCell>
                    <TableCell>{t('trips.latitude')}</TableCell>
                    <TableCell>{t('trips.longitude')}</TableCell>
                    <TableCell>{t('trips.speedKmh')}</TableCell>
                    <TableCell>{t('trips.heading')}</TableCell>
                    <TableCell>{t('trips.map')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {points.map((p: GpsPoint, i: number) => (
                    <TableRow key={i} sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      bgcolor: p.speed === 0 ? '#fefce8' : undefined,
                    }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {fmtTime(p.time)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.latitude.toFixed(5)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.longitude.toFixed(5)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}
                          sx={{ color: p.speed === 0 ? '#94a3b8' : p.speed > 100 ? '#ef4444' : '#0f172a' }}>
                          {p.speed.toFixed(1)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{p.heading.toFixed(0)}°</TableCell>
                      <TableCell>
                        <Link href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`}
                          target="_blank" rel="noopener" sx={{ fontSize: 11 }}>
                          {t('trips.view')}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                  {points.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                        {t('trips.noGpsPoints')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function TripsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)

  const [deviceId, setDeviceId] = useState('')
  const [fromDate, setFromDate] = useState<Date>(subDays(new Date(), 30))
  const [toDate, setToDate] = useState<Date>(new Date())
  const [detailTrip, setDetailTrip] = useState<Trip | null>(null)

  const from = useMemo(() => formatISO(fromDate), [fromDate])
  const to = useMemo(() => formatISO(toDate), [toDate])

  const { data: filtered = [], isLoading } = useTrips(deviceId || undefined, from, to)

  // Build device name lookup
  const deviceNames = useMemo(() => {
    const map = new Map<string, string>()
    devices.forEach((d) => map.set(d.id, d.name))
    return map
  }, [devices])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              {t('trips.title')}
            </Typography>
            <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
              {t('trips.tripCount', { count: filtered.length })}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/trip-comparison')}
            sx={{ color: '#34A8E0', borderColor: '#34A8E0' }}
          >
            {t('trips.compare')}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField select size="small" label={t('fleet.selectDevice')} value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)} sx={{ minWidth: 220 }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('trips.allDevices')}</MenuItem>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <DateTimePicker label={t('playback.from')} value={fromDate} onChange={(v) => v && setFromDate(v)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }} />
            <DateTimePicker label={t('playback.to')} value={toDate} onChange={(v) => v && setToDate(v)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }} />
          </Stack>
        </Card>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress sx={{ color: '#34A8E0' }} />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {filtered.length === 0 && (
              <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <MapIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                <Typography variant="body2" color="#64748b">
                  {t('trips.noTrips')}
                </Typography>
              </Card>
            )}
            {filtered.map((trip) => (
              <Card key={trip.id} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body1" fontWeight={700} color="#1e293b">
                      {deviceNames.get(trip.device_id) ?? trip.device_id.slice(0, 8)}
                    </Typography>
                    <Typography variant="caption" color="#94a3b8" display="block">
                      {fmtDateTimeFull(trip.started_at)}
                      {trip.ended_at && ` — ${fmtDateTimeFull(trip.ended_at)}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip icon={<Speed sx={{ fontSize: 12 }} />}
                      label={`${trip.distance_km.toFixed(1)} km`} size="small"
                      sx={{ fontSize: 11, fontWeight: 600 }} />
                    <Chip icon={<AccessTime sx={{ fontSize: 12 }} />}
                      label={`${Math.round(trip.duration_sec / 60)} min`} size="small"
                      sx={{ fontSize: 11, fontWeight: 600 }} />
                    {trip.max_speed_kph > 0 && (
                      <Chip label={`max ${trip.max_speed_kph.toFixed(0)} km/h`} size="small" variant="outlined"
                        sx={{ fontSize: 11 }} />
                    )}
                    <Chip label={trip.status === 'completed' ? t('trips.completed') : t('trips.active')} size="small" sx={{
                      fontSize: 11, fontWeight: 600,
                      bgcolor: trip.status === 'completed' ? '#f0fdf4' : '#eff6ff',
                      color: trip.status === 'completed' ? '#10b981' : '#3b82f6',
                      border: `1px solid ${trip.status === 'completed' ? '#bbf7d0' : '#bfdbfe'}`,
                    }} />
                    <Button size="small" startIcon={<Info />}
                      onClick={() => { setDetailTrip(trip) }}
                      sx={{ ml: 1, color: '#34A8E0' }}>
                      {t('trips.details')}
                    </Button>
                    <Button size="small" startIcon={<PlayArrow />}
                      onClick={() => { analytics.track(EVENTS.TRIP_PLAYBACK_STARTED, { trip_id: trip.id, device_id: trip.device_id }); navigate(`/playback?device=${trip.device_id}&from=${encodeURIComponent(trip.started_at)}&to=${encodeURIComponent(trip.ended_at || new Date().toISOString())}`) }}>
                      {t('trips.replay')}
                    </Button>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        )}
        {detailTrip && (
          <TripDetailDialog
            trip={detailTrip}
            deviceName={deviceNames.get(detailTrip.device_id) ?? detailTrip.device_id.slice(0, 8)}
            open={!!detailTrip}
            onClose={() => setDetailTrip(null)}
          />
        )}
      </Box>
    </LocalizationProvider>
  )
}
